import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, addDoc, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import jsPDF from 'jspdf';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './OrderConfirmation.css';
import PaymentErrorPage from './PaymentErrorPage';
import { auth } from '../services/firebase';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, totalPrice, clearCart } = useCart();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('pending'); // 'pending', 'success', 'error'
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);

  // Helper to get query param
  const getQueryParam = (name) => {
    return new URLSearchParams(location.search).get(name);
  };

  // Generate PDF Receipt
  const generatePDFReceipt = () => {
    if (!orderData) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(24);
    doc.setTextColor(0, 0, 0);
    doc.text('GREY COFFEE', 105, 30, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text('EST. 2020', 105, 40, { align: 'center' });
    
    // Receipt Title
    doc.setFontSize(18);
    doc.text('Receipt', 105, 60, { align: 'center' });
    
    // Order Info
    doc.setFontSize(12);
    doc.text(`Order #: ${orderData.id.slice(-8).toUpperCase()}`, 20, 80);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 90);
    doc.text(`Time: ${new Date().toLocaleTimeString()}`, 20, 100);
    
    // Customer Info
    if (orderData.shipping) {
      doc.text(`Customer: ${orderData.shipping.firstName} ${orderData.shipping.lastName}`, 20, 120);
      doc.text(`Email: ${orderData.shipping.email}`, 20, 130);
      doc.text(`Phone: ${orderData.shipping.phone}`, 20, 140);
    }
    
    // Items Header
    doc.setFontSize(14);
    doc.text('Items:', 20, 160);
    
    // Items List
    doc.setFontSize(10);
    let yPos = 175;
    let subtotal = 0;
    
    orderData.items.forEach((item, index) => {
      const itemTotal = (item.selectedWeight?.price ? parseFloat(item.selectedWeight.price) : item.price) * item.quantity;
      subtotal += itemTotal;
      
      doc.text(`${item.quantity}x ${item.name}`, 25, yPos);
      
      if (item.selectedWeight?.label || item.selectedBeanType) {
        yPos += 8;
        let variant = '';
        if (item.selectedWeight?.label) variant += item.selectedWeight.label;
        if (item.selectedBeanType) {
          if (variant) variant += ' • ';
          variant += item.selectedBeanType.charAt(0).toUpperCase() + item.selectedBeanType.slice(1);
        }
        doc.setTextColor(128, 128, 128);
        doc.text(`   ${variant}`, 25, yPos);
        doc.setTextColor(0, 0, 0);
      }
      
      doc.text(`QAR ${itemTotal.toLocaleString()}`, 150, yPos - (item.selectedWeight?.label || item.selectedBeanType ? 8 : 0));
      yPos += 15;
    });
    
    // Totals
    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 10;
    
    doc.setFontSize(12);
    doc.text('Subtotal:', 130, yPos);
    doc.text(`QAR ${subtotal.toLocaleString()}`, 160, yPos);
    
    yPos += 10;
    doc.text('Shipping:', 130, yPos);
    doc.text('Free', 160, yPos);
    
    yPos += 15;
    doc.setFontSize(14);
    doc.text('Total:', 130, yPos);
    doc.text(`QAR ${subtotal.toLocaleString()}`, 160, yPos);
    
    // Footer
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for your order!', 105, yPos + 30, { align: 'center' });
    doc.text('Visit us at grey-coffee.com', 105, yPos + 40, { align: 'center' });
    
    // Save PDF
    doc.save(`Grey-Coffee-Receipt-${orderData.id.slice(-8).toUpperCase()}.pdf`);
  };

  useEffect(() => {
    // Try multiple sources for transaction/order ID
    let transactionId = getQueryParam('transactionId'); // From our internal redirect
    let orderId = getQueryParam('orderId'); // From our internal redirect
    
    // Check SkipCash return parameters
    const skipcashTransId = getQueryParam('transId'); // SkipCash transaction ID
    const skipcashPaymentId = getQueryParam('id'); // SkipCash payment ID
    const skipcashStatus = getQueryParam('status'); // SkipCash status
    const skipcashStatusId = getQueryParam('statusId'); // SkipCash status ID
    const skipcashAmount = getQueryParam('amount'); // SkipCash payment amount
    
    // If we have SkipCash parameters, use them
    if (skipcashTransId) {
      transactionId = skipcashTransId;
    }
    
    // Also check localStorage as fallback
    if (!transactionId) {
      transactionId = localStorage.getItem('orderTransactionId');
    }
    
         // Log parameters for debugging
     console.log('OrderConfirmation parameters:', {
       transactionId,
       orderId,
       skipcashTransId,
       skipcashPaymentId,
       skipcashStatus,
       skipcashStatusId,
       skipcashAmount,
       localStorage: localStorage.getItem('orderTransactionId'),
       fullURL: window.location.href,
       searchParams: window.location.search
     });
     
     // Debug: List all localStorage keys for pending orders
     console.log('All localStorage keys:');
     for (let i = 0; i < localStorage.length; i++) {
       const key = localStorage.key(i);
       if (key && key.startsWith('pendingOrder_')) {
         console.log(`- ${key}:`, localStorage.getItem(key));
       }
     }
    
    if (!transactionId && !orderId) {
      // Provide more helpful error message with debugging info
      let debugInfo = '';
      if (window.location.search) {
        debugInfo = ` URL parameters: ${window.location.search}`;
      }
      
      setStatus('error');
      setError(`No transaction or order ID found. This may indicate a configuration issue with the payment system.${debugInfo} Please contact support.`);
      return;
    }

    const saveOrder = async () => {
      // Get transaction ID early for duplicate checking
      const finalTransactionId = transactionId || skipcashTransId || localStorage.getItem('orderTransactionId') || `ORDER-${Date.now()}`;
      
      // Check if we're already processing this transaction (prevent multiple simultaneous requests)
      const processingKey = `processing_${finalTransactionId}`;
      if (sessionStorage.getItem(processingKey)) {
        console.log('⏹️ DUPLICATE PREVENTED: Order creation already in progress for transaction:', finalTransactionId);
        return;
      }
      
      // Mark as processing
      console.log('🔄 Starting order creation for transaction:', finalTransactionId);
      sessionStorage.setItem(processingKey, 'true');
      
      // Define SkipCash confirmation check at function scope for both try/catch access
      const hasSkipCashConfirmation = skipcashPaymentId && skipcashStatusId === '2';
      
      try {
        // If we have SkipCash status, validate it's successful
        if (skipcashStatusId && parseInt(skipcashStatusId, 10) !== 2) {
          setStatus('error');
          setError(`Payment was not successful. Status: ${skipcashStatus || 'Unknown'}. Please try again or contact support.`);
          sessionStorage.removeItem(processingKey); // Clean up on error
          return;
        }
        
        // Get shipping data from multiple sources
        let shippingData = location.state?.formData || {};
        
        // Try to get from localStorage if not in location state
        if (!shippingData.firstName) {
          const storedFormData = localStorage.getItem('checkoutFormData');
          if (storedFormData) {
            shippingData = JSON.parse(storedFormData);
          }
        }
        
        // Try to recover from comprehensive backup if available
        let backupData = null;
        
        // Check for recovery data from PaymentRecovery page
        const recoveryData = sessionStorage.getItem('recoveryOrderData');
        if (recoveryData) {
          try {
            backupData = JSON.parse(recoveryData);
            sessionStorage.removeItem('recoveryOrderData'); // Clean up after use
          } catch (e) {
            console.error('Failed to parse recovery data:', e);
          }
        }
        
        // Fallback to localStorage backup
        if (!backupData) {
          const checkoutBackup = localStorage.getItem('checkoutBackup');
          if (checkoutBackup) {
            try {
              backupData = JSON.parse(checkoutBackup);
            } catch (e) {
              console.error('Failed to parse checkout backup:', e);
            }
          }
        }
        
        // Also check sessionStorage backup
        if (!backupData) {
          const sessionBackup = sessionStorage.getItem('checkoutBackup');
          if (sessionBackup) {
            try {
              backupData = JSON.parse(sessionBackup);
            } catch (e) {
              console.error('Failed to parse session backup:', e);
            }
          }
        }
        
        // Create local copies for manipulation
        let localCartItems = cartItems;
        let localTotalPrice = totalPrice;
        
        // Use backup data if available
        if (backupData) {
          // Use backup data if current data is incomplete
          if (!shippingData.firstName && backupData.formData) {
            shippingData = backupData.formData;
          }
          // Use backup cart items and total if current ones are missing
          if ((!localCartItems || localCartItems.length === 0) && backupData.cartItems) {
            localCartItems = backupData.cartItems;
          }
          if (!localTotalPrice && backupData.totalPrice) {
            localTotalPrice = backupData.totalPrice;
          }
        }
        
        // Additional recovery attempt: Check for pending order by transaction ID
        if ((!localCartItems || localCartItems.length === 0) && transactionId) {
          const pendingOrderKey = `pendingOrder_${transactionId}`;
          const pendingOrderData = localStorage.getItem(pendingOrderKey);
          if (pendingOrderData) {
            try {
              const pendingOrder = JSON.parse(pendingOrderData);
              console.log('Found pending order data:', pendingOrder);
              if (pendingOrder.cartItems && pendingOrder.cartItems.length > 0) {
                localCartItems = pendingOrder.cartItems;
              }
              if (pendingOrder.totalPrice) {
                localTotalPrice = pendingOrder.totalPrice;
              }
              if (pendingOrder.formData && !shippingData.firstName) {
                shippingData = pendingOrder.formData;
              }
            } catch (e) {
              console.error('Failed to parse pending order data:', e);
            }
          }
        }
        
        // ENHANCED: For SkipCash redirects, mimic the manual recovery flow
        if (hasSkipCashConfirmation && (!localCartItems || localCartItems.length === 0) && transactionId) {
          console.log('SkipCash redirect detected, searching all pending orders for match...');
          console.log('Looking for transaction ID:', transactionId);
          
          let foundMatch = false;
          // Search through all pending orders to find matching transaction
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('pendingOrder_')) {
              try {
                const orderData = JSON.parse(localStorage.getItem(key));
                const keyTransactionId = key.replace('pendingOrder_', '');
                
                console.log(`Checking ${key}:`, {
                  keyTransactionId,
                  orderDataTransactionId: orderData.transactionId,
                  targetTransactionId: transactionId
                });
                
                // Check if this pending order matches our transaction
                if (keyTransactionId === transactionId || 
                    orderData.transactionId === transactionId ||
                    keyTransactionId === transactionId.replace('ORDER-', '') ||
                    orderData.transactionId === transactionId.replace('ORDER-', '')) {
                  
                  console.log('✅ FOUND MATCHING PENDING ORDER:', orderData);
                  foundMatch = true;
                  
                  // Use this data (same as manual recovery)
                  if (orderData.cartItems && orderData.cartItems.length > 0) {
                    localCartItems = orderData.cartItems;
                    console.log('✅ Recovered cart items:', localCartItems.length);
                  }
                  if (orderData.totalPrice) {
                    localTotalPrice = orderData.totalPrice;
                    console.log('✅ Recovered total price:', localTotalPrice);
                  }
                  if (orderData.formData && !shippingData.firstName) {
                    shippingData = orderData.formData;
                    console.log('✅ Recovered form data:', shippingData);
                  }
                  break;
                }
              } catch (e) {
                console.error('Failed to parse pending order during search:', e);
              }
            }
          }
          
          if (!foundMatch) {
            console.log('❌ No matching pending order found for transaction:', transactionId);
          }
        }
        
        // For SkipCash confirmations, try to proceed even with missing data
        if (hasSkipCashConfirmation) {
          console.log('SkipCash payment confirmed, proceeding with order creation even if data is incomplete');
        } else {
          // Only enforce strict validation for non-SkipCash flows
          if (!shippingData.firstName && !currentUser?.displayName) {
            setStatus('error');
            setError('Missing customer information. Please try placing your order again.');
            sessionStorage.removeItem(processingKey); // Clean up on error
            return;
          }
          
          if (!localCartItems || localCartItems.length === 0) {
            setStatus('error');
            setError('No items found in your order. Your session may have expired. Please try placing your order again.');
            sessionStorage.removeItem(processingKey); // Clean up on error
            return;
          }
        }
        
        // Create fallback customer info if missing but payment confirmed
        if (hasSkipCashConfirmation && !shippingData.firstName && !currentUser?.displayName) {
          shippingData = {
            firstName: 'SkipCash',
            lastName: 'Customer',
            email: 'customer@skipcash.payment',
            phone: 'N/A',
            street: 'N/A',
            city: 'Doha',
            state: 'DH',
            country: 'QA',
            postalCode: '00000'
          };
        }
        
        // Create fallback cart items if missing but payment confirmed
        let orderItems = localCartItems;
        let orderTotal = localTotalPrice || 0;
        
        // If we have SkipCash amount, use it as the actual payment amount
        if (skipcashAmount && !isNaN(parseFloat(skipcashAmount))) {
          orderTotal = parseFloat(skipcashAmount);
        }
        
        if (hasSkipCashConfirmation && (!localCartItems || localCartItems.length === 0)) {
          orderItems = [{
            id: 'skipcash-payment',
            name: 'Payment Recovery - Missing Cart Data',
            price: orderTotal,
            quantity: 1,
            image: '/images/logo.png',
            note: 'Original cart data lost during payment process'
          }];
        }
        
        // Check if order already exists with this transaction ID
        const existingOrderQuery = query(
          collection(db, 'orders'), 
          where('transactionId', '==', finalTransactionId)
        );
        const existingOrderSnap = await getDocs(existingOrderQuery);
        
        if (!existingOrderSnap.empty) {
          console.log('Order with this transaction ID already exists:', finalTransactionId);
          // Use the existing order instead of creating a duplicate
          const existingOrder = existingOrderSnap.docs[0];
          setOrderData({ id: existingOrder.id, ...existingOrder.data() });
          setStatus('success');
          
          // Clean up processing flag and pending order
          sessionStorage.removeItem(processingKey);
          localStorage.removeItem(`pendingOrder_${finalTransactionId}`);
          return;
        }
        
        const orderDocData = {
          userId: currentUser?.uid || null,
          userEmail: shippingData.email || currentUser?.email || '',
          userName: `${shippingData.firstName || ''} ${shippingData.lastName || ''}`.trim() || currentUser?.displayName || '',
          shipping: shippingData,
          items: orderItems,
          total: orderTotal,
          transactionId: finalTransactionId,
          status: 'paid',
          paymentMethod: 'skipcash',
          createdAt: new Date(),
          notifiedAdmin: false,
          // Add SkipCash payment details if available
          ...(skipcashPaymentId && { skipcashPaymentId }),
          ...(skipcashStatusId && { skipcashStatusId: parseInt(skipcashStatusId, 10) }),
          ...(skipcashStatus && { skipcashStatus }),
          paidAt: new Date(), // Mark as paid immediately since SkipCash confirmed payment
          // Flag to indicate this was created from SkipCash callback with missing data
          ...(hasSkipCashConfirmation && (!localCartItems || localCartItems.length === 0) && { 
            incompleteOrder: true,
            note: 'Order created from SkipCash payment confirmation with missing cart data'
          }),
        };

        // Save order to Firestore
        let orderRef;
        try {
          orderRef = await addDoc(collection(db, 'orders'), orderDocData);
        } catch (firestoreError) {
          console.error('Firestore save failed:', firestoreError);
          if (hasSkipCashConfirmation) {
            // For SkipCash payments that succeeded but Firestore failed
            setStatus('error');
            setError('Payment was successful but we had trouble saving your order. Please contact support with your payment ID: ' + skipcashPaymentId);
            return;
          }
          throw firestoreError;
        }
        
        // Add the ID to the order data
        const completeOrderData = {
          ...orderDocData,
          id: orderRef.id
        };
        
        setOrderData(completeOrderData);

        // Notify admin about new order
        await addDoc(collection(db, 'adminNotifications'), {
          type: 'new_order',
          orderId: orderRef.id,
          message: `New order received from ${orderDocData.userName || 'Customer'}`,
          amount: orderTotal,
          createdAt: new Date(),
          read: false
        });

        // Mark order as admin notified
        await updateDoc(doc(db, 'orders', orderRef.id), {
          notifiedAdmin: true
        });

        // Send order notification to admin automatically
        try {
          // Get the current user's ID token
          const idToken = await auth.currentUser.getIdToken();
          
          console.log('Sending order notification to admin for order:', orderRef.id);
          const response = await fetch('https://us-central1-greycoffee-f7f71.cloudfunctions.net/sendOrderNotification', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              orderId: orderRef.id
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          console.log('Order notification sent successfully to admin');
        } catch (notificationError) {
          console.error('Failed to send order notification to admin:', notificationError);
          // Don't fail the order creation if notification fails
        }

        // Clear cart and cleanup localStorage
        await clearCart();
        
        // Clean up localStorage after successful order
        localStorage.removeItem('checkoutFormData');
        localStorage.removeItem('orderTransactionId');
        localStorage.removeItem('checkoutBackup');
        sessionStorage.removeItem('checkoutBackup');
        
        // Clean up the specific pending order
        localStorage.removeItem(`pendingOrder_${finalTransactionId}`);
        // Also try to clean up if it's in the ORDER-xxx format
        if (finalTransactionId.startsWith('ORDER-')) {
          localStorage.removeItem(`pendingOrder_${finalTransactionId}`);
        } else {
          localStorage.removeItem(`pendingOrder_ORDER-${finalTransactionId}`);
        }
        
        // Clean up processing flag
        sessionStorage.removeItem(processingKey);
        
        setStatus('success');
      } catch (err) {
        console.error('Error saving order:', err);
        
        // For SkipCash confirmations, still show success but note the issue
        if (hasSkipCashConfirmation) {
          console.log('SkipCash payment confirmed but order save failed, redirecting to payment recovery');
          sessionStorage.removeItem(processingKey); // Clean up before navigation
          navigate(`/payment-recovery?paymentId=${skipcashPaymentId}&amount=${skipcashAmount || '0'}`);
          return;
        }
        
        setStatus('error');
        setError('Failed to save order. Please contact support with your transaction ID.');
        
        // Clean up processing flag on error to allow retry
        sessionStorage.removeItem(processingKey);
      }
    };

    saveOrder();
    // eslint-disable-next-line
  }, []); // Empty dependency array - run only once

  useEffect(() => {
    if (status === 'success' && orderData?.id) {
      navigate('/order-success', {
        state: {
          orderData: {
            orderId: orderData.id,
            total: orderData.total,
            phone: orderData.shipping?.phone,
            shipping: orderData.shipping
          }
        }
      });
    }
  }, [status, orderData, navigate]);

  if (status === 'pending') {
    return (
      <div className="order-confirmation-page">
        <Header />
        <div className="order-confirmation-container">
          <div className="order-confirmation-card">
            <div className="loading-spinner"></div>
            <h2>Processing your order...</h2>
            <p>Please wait while we confirm your payment and prepare your receipt.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (status === 'error') {
    // Get SkipCash params for error page
    const urlParams = new URLSearchParams(location.search);
    const skipcashPaymentId = urlParams.get('id');
    const skipcashStatusId = urlParams.get('statusId');
    const skipcashStatus = urlParams.get('status');
    return (
      <PaymentErrorPage
        skipcashStatusId={skipcashStatusId}
        skipcashStatus={skipcashStatus}
        skipcashPaymentId={skipcashPaymentId}
      />
    );
  }

  return (
    <div className="order-confirmation-page">
      <Header />
      <div className="order-confirmation-container">
        <div className="order-confirmation-card success">
          {/* Success Icon */}
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
          </div>

          {/* Thank You Message */}
          <h1>Thank you for your order!</h1>
          <p className="subtitle">Here is your receipt from Grey Coffee</p>

          {/* Order Summary */}
          <div className="order-summary">
            <div className="order-header">
              <h3>Order Summary</h3>
              <span className="order-id">#{orderData?.id.slice(-8).toUpperCase()}</span>
            </div>

            <div className="order-items">
              {orderData?.items.map((item, index) => (
                <div key={index} className="order-item">
                  <img src={item.image} alt={item.name} className="item-image" />
                  <div className="item-details">
                    <h4>{item.name}</h4>
                    {(item.selectedWeight?.label || item.selectedBeanType) && (
                      <span className="item-variant">
                        {item.selectedWeight?.label ? `${item.selectedWeight.label}` : ''}
                        {item.selectedWeight?.label && item.selectedBeanType ? ' • ' : ''}
                        {item.selectedBeanType ? `${item.selectedBeanType.charAt(0).toUpperCase() + item.selectedBeanType.slice(1)}` : ''}
                      </span>
                    )}
                    <span className="item-quantity">Qty: {item.quantity}</span>
                  </div>
                  <div className="item-price">
                    QAR {(item.selectedWeight?.price ? parseFloat(item.selectedWeight.price) : item.price).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            <div className="order-totals">
              <div className="total-line">
                <span>Subtotal</span>
                <span>QAR {(orderData?.total || 0).toLocaleString()}</span>
              </div>
              <div className="total-line">
                <span>Shipping</span>
                <span>Free</span>
              </div>
              <div className="total-line final">
                <span>Total</span>
                <span>QAR {(orderData?.total || 0).toLocaleString()}</span>
              </div>
              {orderData?.incompleteOrder && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  background: '#fff3cd', 
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#856404'
                }}>
                  ⚠️ This order was recovered from payment confirmation. Some details may be incomplete.
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button onClick={generatePDFReceipt} className="download-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Download PDF Receipt
            </button>
            
            <button onClick={() => navigate('/products')} className="continue-shopping-btn">
              Continue Shopping
            </button>
            
            <button onClick={() => navigate('/')} className="home-btn">
              Back to Home
            </button>
          </div>

          {/* Additional Info */}
          <div className="additional-info">
            <p>📧 A confirmation email has been sent to your email address.</p>
            <p>📦 Your order will be processed and shipped within 1-2 business days.</p>
            <p>🚚 You will receive tracking information once your order ships.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderConfirmation; 